import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { CreateStockAdjustmentRequest } from "../Dto/CreateStockAdjustmentRequest";
import { Stock } from "../Dto/StockModel";
import { StockMovement } from "../Dto/StockMovementModel";
import { StockMovementSearchResponse } from "../Dto/StockMovementSearchResponse";
import { StockSummaryRequest } from "../Dto/StockSummaryRequest";
import { StockSummaryResponse } from "../Dto/StockSummaryResponse";
import { sortByMaterialSequence } from "../../../Common/Utility/UtilityMaterialSequence";

@Injectable({ providedIn: "root" })
export class StockApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchStockSummary(params: StockSummaryRequest): Promise<StockSummaryResponse> {
    const url = new URL("stocks/summary", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) searchParams.set("search", params.search);
    if (params.warehouseId) searchParams.set("warehouseId", params.warehouseId);
    if (params.page !== undefined) searchParams.set("page", params.page.toString());
    if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    const response = await firstValueFrom(this.http.get<StockSummaryResponse>(url.toString(), { withCredentials: true }));
    return {
      ...response,
      data: sortByMaterialSequence(response.data ?? [], (item) => item.material ?? {}),
    };
  }

  async createAdjustment(payload: CreateStockAdjustmentRequest): Promise<Stock> {
    const url = new URL("stocks/adjustments", `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<Stock>(url.toString(), payload, { withCredentials: true }));
    return this.normalizeStock(response);
  }

  async getStockById(id: string): Promise<Stock | null> {
    const url = new URL(`stocks/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.get<Stock | null>(url.toString(), { withCredentials: true }));
    return (() => {
      if (response) {
        return this.normalizeStock(response);
      }
      return null;
    })();
  }

  async getStockMovements(stockId: string, page = 1, limit = 20): Promise<StockMovementSearchResponse> {
    const url = new URL(`stocks/${stockId}/movements`, `${this.baseUrl}/`);
    url.search = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    }).toString();

    const response = await firstValueFrom(this.http.get<StockMovementSearchResponse>(url.toString(), { withCredentials: true }));

    return {
      ...response,
      data: response.data.map((movement) => this.normalizeMovement(movement)),
    };
  }

  private normalizeStock(item: Stock): Stock {
    const materialRecord = (() => {
      if (item.materialId && typeof item.materialId === "object") {
        return item.materialId as unknown as { id?: string; name?: string; sku?: string };
      }
      return null;
    })();
    const warehouseRecord = (() => {
      if (item.warehouseId && typeof item.warehouseId === "object") {
        return item.warehouseId as unknown as { id?: string; name?: string };
      }
      return null;
    })();

    return {
      ...item,
      materialId: (() => {
        if (typeof item.materialId === "string") {
          return item.materialId;
        }
        return materialRecord?.id ?? item.materialId;
      })(),
      warehouseId: (() => {
        if (typeof item.warehouseId === "string") {
          return item.warehouseId;
        }
        return warehouseRecord?.id ?? item.warehouseId;
      })(),
      material: item.material ?? {
        sequence: Number((materialRecord as { sequence?: number } | null)?.sequence ?? 0),
        name: materialRecord?.name ?? "",
        sku: materialRecord?.sku ?? "",
      },
      warehouse: item.warehouse ?? {
        name: warehouseRecord?.name ?? "",
      },
    };
  }

  private normalizeMovement(item: StockMovement): StockMovement {
    return {
      ...item,
      id: this.normalizeRelationId(item.id),
      materialId: (() => {
        if (item.materialId) {
          return this.normalizeRelationId(item.materialId);
        }
        return undefined;
      })(),
      warehouseId: (() => {
        if (item.warehouseId) {
          return this.normalizeRelationId(item.warehouseId);
        }
        return undefined;
      })(),
    };
  }

  private normalizeRelationId(value: string | { id?: string }): string {
    return (() => {
      if (typeof value === "string") {
        return value;
      }
      return value.id ?? "";
    })();
  }
}
